import React, { useState, useEffect } from 'react';
import { Search, UserPlus, UserMinus, Users, Star, Eye } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Avatar from '../components/Avatar';

interface UserProfile {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  bio?: string;
  role: 'creator' | 'consumer';
  isSubscribed: boolean;
  monthlyPrice?: number;
  yearlyPrice?: number;
  subscriberCount?: number;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const ProfileSearch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [creators, setCreators] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'discover' | 'search'>('discover');
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });

  // Load creator profiles on component mount and handle URL queries
  useEffect(() => {
    loadCreators();
    
    // Check for search query in URL parameters
    const urlParams = new URLSearchParams(location.search);
    const queryParam = urlParams.get('q');
    if (queryParam) {
      setSearchQuery(queryParam);
      setActiveTab('search');
      // Perform search after a short delay to ensure state is set
      setTimeout(() => {
        searchProfilesWithQuery(queryParam);
      }, 100);
    }
  }, [location.search]);

  const loadCreators = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/creators?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCreators(data.creators);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error loading creators:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchProfilesWithQuery = async (query: string) => {
    if (!query.trim()) return;
    
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Searching with token:', token ? 'present' : 'missing');
      const response = await fetch(`/api/users/search?query=${encodeURIComponent(query)}&page=1&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔍 Search response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Search results:', data);
        setSearchResults(data.users || []);
        setActiveTab('search');
      } else {
        const errorData = await response.json();
        console.error('Search failed:', response.status, errorData);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching profiles:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const searchProfiles = async () => {
    searchProfilesWithQuery(searchQuery);
  };

  const handleSubscribe = async (userId: string, type: 'monthly' | 'annual', amount: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          type,
          amount
        })
      });

      if (response.ok) {
        // Refresh data to update subscription status
        if (activeTab === 'discover') {
          loadCreators(pagination.page);
        } else {
          searchProfiles();
        }
        alert('Subscription created successfully!');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('Failed to create subscription');
    }
  };

  const handleUnsubscribe = async (userId: string) => {
    try {
      const token = localStorage.getItem('token');
      // First, get user's subscriptions to find the subscription ID
      const subscriptionsResponse = await fetch('/api/subscriptions/my-subscriptions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (subscriptionsResponse.ok) {
        const subscriptionsData = await subscriptionsResponse.json();
        const subscription = subscriptionsData.subscriptions.find((sub: any) => sub.user.id === userId);
        
        if (subscription) {
          const cancelResponse = await fetch(`/api/subscriptions/${subscription.id}/cancel`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (cancelResponse.ok) {
            // Refresh data to update subscription status
            if (activeTab === 'discover') {
              loadCreators(pagination.page);
            } else {
              searchProfiles();
            }
            alert('Subscription cancelled successfully!');
          } else {
            const errorData = await cancelResponse.json();
            alert(errorData.error || 'Failed to cancel subscription');
          }
        }
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription');
    }
  };

  const ProfileCard: React.FC<{ profile: UserProfile }> = ({ profile }) => (
    <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-4">
        <Avatar 
          src={profile.avatar} 
          alt={profile.username} 
          size="lg"
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              @{profile.username}
            </h3>
            {profile.role === 'creator' && (
              <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
            )}
          </div>
          
          {profile.bio && (
            <p className="text-sm text-gray-700 mt-2 line-clamp-2">{profile.bio}</p>
          )}
          <p className="text-sm text-gray-500 truncate">{profile.email}</p>
          
          {profile.role === 'creator' && (
            <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
              {profile.subscriberCount !== undefined && (
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{profile.subscriberCount} subscribers</span>
                </div>
              )}
              {profile.monthlyPrice && (
                <span className="text-green-600 font-medium">
                  {profile.monthlyPrice} SOL/month
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="flex flex-col space-y-2">
          <button
            onClick={() => navigate(`/profile/${profile.id}`)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <Eye className="w-4 h-4 mr-1" />
            View Profile
          </button>
          {profile.isSubscribed ? (
            <button
              onClick={() => handleUnsubscribe(profile.id)}
              className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <UserMinus className="w-4 h-4 mr-1" />
              Unsubscribe
            </button>
          ) : (
            profile.role === 'creator' && profile.monthlyPrice && (
              <button
                onClick={() => navigate(`/profile/${profile.id}`)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Subscribe
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Discover Creators</h1>
          
          {/* Search bar */}
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchProfiles()}
                placeholder="Search by username, email, or full name..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={searchProfiles}
              disabled={!searchQuery.trim() || searchLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('discover')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'discover'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Discover Creators
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Search Results {searchResults.length > 0 && `(${searchResults.length})`}
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'discover' && (
          <div>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading creators...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {creators.map((creator) => (
                  <ProfileCard key={creator.id} profile={creator} />
                ))}
                
                {creators.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No creators found.</p>
                  </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-8">
                    <button
                      onClick={() => loadCreators(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-700">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => loadCreators(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'search' && (
          <div>
            {searchResults.length > 0 ? (
              <div className="space-y-4">
                {searchResults.map((profile) => (
                  <ProfileCard key={profile.id} profile={profile} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Search className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No search results</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery ? 'Try searching for different terms.' : 'Start typing to search for profiles.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSearch;